import { useCallback, useMemo, useState } from "react";
import {
  type ColetasView,
  type DateWindow,
  canStepNext,
  canStepPrev,
  formatPeriodLabel,
  getWindowForView,
  startOfToday,
  stepRef,
} from "@/utils/coletasCalendar";

export interface UseColetasCalendarNavResult {
  view: ColetasView;
  setView: (view: ColetasView) => void;
  referenceDate: Date;
  window: DateWindow;
  periodLabel: string;
  goNext: () => void;
  goPrev: () => void;
  goToday: () => void;
  /** Falso quando o período atual já é o mais antigo permitido (hoje). */
  canGoPrev: boolean;
  /** Falso quando avançar sairia do mês atual (semana) ou sempre (mês). */
  canGoNext: boolean;
}

/**
 * Controla a navegação do calendário de Próximas Coletas (visão + data de
 * referência) e deriva a janela de datas visível e o rótulo do período.
 */
export function useColetasCalendarNav(
  initialView: ColetasView = "agenda",
): UseColetasCalendarNavResult {
  const [view, setViewRaw] = useState<ColetasView>(initialView);
  const [referenceDate, setReferenceDate] = useState<Date>(() => startOfToday());

  // Ao trocar de visão ancora a referenceDate no início do período visível atual,
  // evitando que semana "jun 30–jul 6" (ref=jul 3) abra o mês de julho.
  // O clamp em `today` garante que a referência nunca retroceda para antes de
  // hoje (ex.: trocar Semana→Agenda na quarta ancora na segunda, que é passado).
  const setView = useCallback((newView: ColetasView) => {
    setReferenceDate((current) => {
      const anchor = getWindowForView(view, current).start;
      const today = startOfToday();
      return anchor < today ? today : anchor;
    });
    setViewRaw(newView);
  }, [view]);

  const window = useMemo(
    () => getWindowForView(view, referenceDate),
    [view, referenceDate],
  );
  const periodLabel = useMemo(
    () => formatPeriodLabel(view, referenceDate),
    [view, referenceDate],
  );

  const canGoPrev = useMemo(
    () => canStepPrev(view, referenceDate),
    [view, referenceDate],
  );

  const canGoNext = useMemo(
    () => canStepNext(view, referenceDate),
    [view, referenceDate],
  );

  const goNext = useCallback(
    () => setReferenceDate((current) => stepRef(view, current, 1)),
    [view],
  );
  const goPrev = useCallback(
    () =>
      setReferenceDate((current) =>
        canStepPrev(view, current) ? stepRef(view, current, -1) : current,
      ),
    [view],
  );
  const goToday = useCallback(() => setReferenceDate(startOfToday()), []);

  return {
    view,
    setView,
    referenceDate,
    window,
    periodLabel,
    goNext,
    goPrev,
    goToday,
    canGoPrev,
    canGoNext,
  };
}
